import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import { InnerCard } from "@repo/ui";
import { withTranslations } from "../../../../hoc/withTranslations/withTranslations";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import { registrationSteps } from "../../constants/steps";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import withAuth from "../../../../hoc/withAuth/withAuth";
import EditIcon from "../../../../assets/icons/edit.svg";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import { getPhoto } from "../../../../utils/indexedDB";
import {
  generateUploadURL,
  uploadMedia,
  verifyMediaUpload,
} from "../../../../services/mediaApiService";
import { createGiftTable } from "../../../../services/giftTableApiService";
import { updateGiftTableState } from "../../../../redux/slices/giftTableSlice";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../hooks/useAppDispatch";
import LoadingButton from "../../../../components/LoadingButton/LoadingButton";
import { eventTypeLocale } from "../../../../constants/eventTypeLocale";
import { themeLocale } from "../../../../constants/themeLocale";
import * as Sentry from "@sentry/react";

const LOCALE = "modules_Registration_pages_GiftTableSummary_GiftTableSummary";

const GiftTableSummary: React.FC = () => {
  const { t } = useTranslation(LOCALE);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const giftTable = useAppSelector((state: RootState) => state.giftTable);
  const auth = useSelector((state: RootState) => state.auth);
  const [photo, setPhoto] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const savedPhoto = await getPhoto("giftTablePhoto");
      if (savedPhoto) {
        setPhoto(savedPhoto);
      }
    })();
  }, []);

  const createThumbnail = async (blob: Blob, width: number): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(blob);
      img.onload = () => {
        const ratio = img.height / img.width;
        const height = Math.round(width * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          const err = new Error("no context");
          Sentry.captureException(err);
          return reject(err);
        }
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((thBlob) => {
          if (!thBlob) {
            const err = new Error("no blob");
            Sentry.captureException(err);
            return reject(err);
          }
          resolve(
            new File([thBlob], `thumb-${width}.jpg`, { type: "image/jpeg" })
          );
        }, "image/jpeg");
      };
      img.onerror = (err) => {
        Sentry.captureException(err);
        reject(err);
      };
    });
  };

  const handleCreateGiftTable = async () => {
    setIsLoading(true);
    try {
      setProgress(10);

      const savedPhoto = await getPhoto("giftTablePhoto");
      if (!savedPhoto) throw new Error("Profile picture not found");

      setProgress(40);

      const extension = savedPhoto.split(";")[0].split("/")[1];
      const dateTime = new Date().toISOString().replace(/[:.-]/g, "");
      const userId = auth.user?.id as string;
      const fileName = `${dateTime}-${userId}-gift-table-cover.${extension}`;

      const { presignedURL, mediaID } = await generateUploadURL(
        fileName,
        userId
      );
      setProgress(70);

      const response = await fetch(savedPhoto);
      const blob = await response.blob();

      await uploadMedia(presignedURL, new File([blob], fileName));
      await verifyMediaUpload(mediaID);

      const thumbSmall = await createThumbnail(blob, 300);
      const upSmall = await generateUploadURL(`thumb-s-${fileName}`, userId);
      await uploadMedia(upSmall.presignedURL, thumbSmall);
      const verSmall = await verifyMediaUpload(upSmall.mediaID);

      const thumbMed = await createThumbnail(blob, 600);
      const upMed = await generateUploadURL(`thumb-m-${fileName}`, userId);
      await uploadMedia(upMed.presignedURL, thumbMed);
      const verMed = await verifyMediaUpload(upMed.mediaID);

      const thumbLg = await createThumbnail(blob, 1200);
      const upLg = await generateUploadURL(`thumb-l-${fileName}`, userId);
      await uploadMedia(upLg.presignedURL, thumbLg);
      const verLg = await verifyMediaUpload(upLg.mediaID);
      setProgress(90);

      const id = await createGiftTable({
        user_id: userId,
        amphitryon: giftTable.amphitryon,
        event_name: giftTable.eventName,
        event_type: giftTable.eventType,
        event_date: giftTable.eventDate,
        event_location: {
          address: giftTable.eventLocation.address,
          lat: giftTable.eventLocation.lat,
          lng: giftTable.eventLocation.lng,
        },
        event_place: giftTable.eventPlace,
        message_to_guests: giftTable.messageToGuests,
        organizers: giftTable.organizers.map((organizer) => ({
          first_name: organizer.firstName,
          last_name: organizer.lastName,
        })),
        theme: giftTable.theme!,
        profile_picture: mediaID,
        thumbnail_small: verSmall.id,
        thumbnail_med: verMed.id,
        thumbnail_lg: verLg.id,
        closed: false,
        closed_at: "",
        products: [],
        slug: giftTable.slug,
      });

      dispatch(updateGiftTableState({ ID: id }));
      setProgress(100);
      navigate("/registration/create-gift-table/success");
    } catch (error) {
      Sentry.captureException(error);
      console.error("Failed to create gift table:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <StepIndicator
        steps={registrationSteps}
        currentStep={2}
        progressPercentage={75}
      />
      <AnimatedCard title={t("title")} className="bg-white max-w-lg w-full">
        <p className="text-center">{t("introduction")}</p>
        <InnerCard className="relative flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex flex-col items-center space-y-2 md:w-1/2">
            <div className="relative w-24 h-24 rounded-full overflow-hidden">
              <img
                src={photo || ""}
                alt="Event"
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-center">{t("eventType")}</p>
            <p className="text-primary text-center">
              {t(eventTypeLocale[giftTable.eventType])}
            </p>
          </div>

          <div className="flex flex-col text-center md:text-left md:w-1/2">
            <div
              className="absolute top-2 right-2 cursor-pointer"
              onClick={() => navigate("/registration/create-gift-table/step1")}
            >
              <img src={EditIcon} alt="Edit" className="w-6 h-6" />
            </div>
            <p>{t("eventName")}</p>
            <p className="text-primary mb-4">{giftTable.eventName}</p>

            <p>{t("organizersLabel", { defaultValue: "Organizadores" })}</p>
            <p className="text-primary mb-4">
              {giftTable.organizers.length
                ? giftTable.organizers
                    .map(
                      (organizer) =>
                        `${organizer.firstName} ${organizer.lastName}`.trim()
                    )
                    .join(", ")
                : t("organizersEmpty", {
                    defaultValue: "Sin organizadores",
                  })}
            </p>

            <p>{t("eventPlace")}</p>
            <p className="text-primary mb-4">{giftTable.eventPlace}</p>

            <p>{t("eventLocation")}</p>
            <p className="text-primary mb-4">
              {giftTable.eventLocation.address}
            </p>

            <p>{t("eventTheme")}</p>
            <p className="text-primary mb-4">
              {t(themeLocale[giftTable.theme])}
            </p>

            <p>{t("eventDate")}</p>
            <p className="text-primary">
              {new Date(giftTable.eventDate).toLocaleDateString()}
            </p>
          </div>
        </InnerCard>

        <LoadingButton
          label={isLoading ? t("processing") : t("createButton")}
          onClick={handleCreateGiftTable}
          progress={progress}
          disabled={isLoading}
          className="w-full mt-4"
        />
      </AnimatedCard>
    </PageContainer>
  );
};

export default withAuth(withTranslations(LOCALE)(GiftTableSummary));
